import React, { useState, useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import moment from 'moment';

import { useTheme } from '@material-ui/core/styles';
import { makeStyles } from '@material-ui/core/styles';

import { showSuccessSnackbar } from 'modules/snackbar';
import { setPortal } from 'modules/portal';
import { makeHeadData } from 'lib/commonLib';
import { paramConverter } from 'modules/common';
import { makeRowColumn, getHeadObj } from 'lib/commonLib';
import { userOnLogin } from 'lib/api/user';
import {
  changeList,
  campaignManageImgList,
  changeStateList,
} from 'lib/api/campaign';

import Icon from 'components/atoms/Icon';
import NewButton from 'components/molecules/NewButton';
import NewFilterWrap from 'components/molecules/NewFilterWrap';
import IconAreaWrap from 'components/molecules/MbTable/IconAreaWrap';
import SpanningTableContents from 'components/molecules/MbTable/SpanningTableContents';
import NewCommonFilter from 'components/organisms/NewCommonFilter';
import FilterCollection from 'components/organisms/FilterCollection';
import NewTableContainer from 'components/molecules/NewTable';
import ToolTipWrap from 'components/molecules/ToolTipWrap';

import Modal from './Modal';

const useStyles = makeStyles((theme) => ({
  circle: {
    width: 15,
    height: 15,
    margin: '0 auto',
    borderRadius: '50%',
  },
  stateTable: {
    '& >  div:first-child': {
      height: 'auto !important',
    },
  },
}));

const Change = () => {
  const { t } = useTranslation();
  const classes = useStyles();
  const theme = useTheme();
  const { portal } = useSelector(({ portal }) => ({
    portal: portal,
  }));
  const dispatch = useDispatch();

  const [initialization, setInitialization] = useState(false);
  const initParams = {
    sort: 'desc',
    order: 'LAST_DATE',
    sDate: moment().format('YYYYMM01'),
    eDate: moment().format('YYYYMMDD'),
    advrtsPrdtCode: '07',
    advrtsTpCode: '05',
    state: 'all', // 게재상태
    changeState: 'all', // 변경상태필터
    contact: 'all',
  };
  const [params, setParams] = useState(initParams);
  const initList = {
    state: [
      // 게재상태
      { label: '전체 게재중', value: 'all' },
      { label: '온라인', value: '01' },
      { label: '모바일', value: '02' },
      { label: '중지', value: '03' },
    ],
    changeState: [
      // 변경상태필터
      { label: '전체', value: 'all' },
      { label: '수정 후 10일 경과 전', value: '01' },
      { label: '수정 10일 경과 후 성과 ↓', value: '02' },
      { label: '수정 10일 경과 후 성과 ↑', value: '03' },
    ],
    changeStateList: {},
  };
  const [list] = useState(initList);
  const [tableList, setTableList] = useState([]);

  const [loading, setLoading] = useState(true);
  const initModal = {
    open: false,
    type: null,
    params: {},
    data: [],
  };
  const [modal, setModal] = useState(initModal);

  const tableHead = {
    headList: [
      {
        id: 'allState',
        label: t('전체'),
        orderFlag: false,
        displayFlag: true,
        type: null,
        format: null,
      },
      {
        id: 'wState',
        label: t('온라인'),
        orderFlag: false,
        displayFlag: true,
        type: null,
        format: null,
      },
      {
        id: 'mState',
        label: t('모바일'),
        orderFlag: false,
        displayFlag: true,
        type: null,
        format: null,
      },
      {
        id: 'stopState',
        label: t('중지'),
        orderFlag: false,
        displayFlag: true,
        type: null,
        format: null,
      },
      {
        id: 'rowState',
        label: t('rowState'),
        orderFlag: false,
        displayFlag: true,
        type: null,
        format: null,
      },
      {
        id: 'normalState',
        label: t('normalState'),
        orderFlag: false,
        displayFlag: true,
        type: null,
        format: null,
      },
      {
        id: 'highState',
        label: t('highState'),
        orderFlag: false,
        displayFlag: true,
        type: null,
        format: null,
      },
    ],
  };

  useEffect(() => {
    setLoading(true);
    changeStateList({ params: { ...paramConverter(params) } })
      .then((response) => {
        const { data } = response.data;
        setTableList([data]);
      })
      .catch((error) => {
        handleClickSnackbar({
          message: error,
          option: {
            type: 'error',
          },
        });
      })
      .finally(() => {
        setLoading(false);
      });
    // eslint-disable-next-line
  }, [params]);

  /**
   * 필터 초기화 후 값을 바꿈
   */
  useEffect(() => {
    if (initialization === true) {
      setInitialization(false);
    } // eslint-disable-next-line
  }, [initialization]);

  /**
   * 필터 초기화
   */
  const handleOnReset = () => {
    if (JSON.stringify(params) !== JSON.stringify(initParams)) {
      setInitialization(true);
      setParams(initParams);
    }
  };

  /**
   * 페이지 파라미터를 관리하는 params을 수정하는 함수
   * @param newParams
   */
  const getParam = (newParams) => {
    if (JSON.stringify(params) !== JSON.stringify(newParams)) {
      setParams(newParams);
    }
  };

  // header 및 body 컬럼 커스텀
  const customized = {
    stateTable: {
      rowState: {
        makeHead: (headsMap) => {
          return (
            <ToolTipWrap title={t('수정 10일 경과 후 성과 ↓')}>
              <div
                className={classes.circle}
                style={{ backgroundColor: theme.palette.trafficLight.red }}
              />
            </ToolTipWrap>
          );
        },
        makeBody: (rowObj, headsMap) => {
          return rowObj['rowState'];
        },
      },
      normalState: {
        makeHead: (headsMap) => {
          return (
            <ToolTipWrap title={t('수정 후 10일 경과 전')}>
              <div
                className={classes.circle}
                style={{ backgroundColor: theme.palette.trafficLight.yellow }}
              />
            </ToolTipWrap>
          );
        },
        makeBody: (rowObj, headsMap) => {
          return rowObj['normalState'];
        },
      },
      highState: {
        makeHead: (headsMap) => {
          return (
            <ToolTipWrap title={t('수정 10일 경과 후 성과 ↑')}>
              <div
                className={classes.circle}
                style={{ backgroundColor: theme.palette.trafficLight.green }}
              />
            </ToolTipWrap>
          );
        },
        makeBody: (rowObj, headsMap) => {
          return rowObj['highState'];
        },
      },
    },
    mainTable: {
      LAST_DATE: {
        makeBody: (rowObj, headsMap) => {
          return rowObj['LAST_DATE'];
        },
      },
      STATE: {
        makeBody: (rowObj, headsMap) => {
          let color;
          if (rowObj['STATE'] === '01') {
            color = theme.palette.trafficLight.yellow;
          } else if (rowObj['STATE'] === '02') {
            color = theme.palette.trafficLight.red;
          } else if (rowObj['STATE'] === '03') {
            color = theme.palette.trafficLight.green;
          }
          return (
            <div
              className={classes.circle}
              style={{ backgroundColor: color }}
            />
          );
        },
      },
      W_STATE: {
        makeBody: (rowObj, headsMap) => {
          return (
            <Icon
              icon="faPowerOff"
              style={
                rowObj.W_STATE === '01'
                  ? { color: '#79bd2f' }
                  : { color: '#6d6d6d' }
              }
            />
          );
        },
      },
      M_STATE: {
        makeBody: (rowObj, headsMap) => {
          return (
            <Icon
              icon="faPowerOff"
              style={
                rowObj.M_STATE === '01'
                  ? { color: '#79bd2f' }
                  : { color: '#6d6d6d' }
              }
            />
          );
        },
      },
      IMG_URL: {
        makeHead: (headsMap) => {
          return t('이미지');
        },
        makeBody: (rowObj, headsMap) => {
          return (
            !!rowObj['IMG_URL'] && (
              <NewButton
                title={t('전체이미지')}
                icon="faImages"
                iconBtn
                onClick={(e) => handleModalOpen(rowObj['SITE_CODE'])}
                disableRipple
                style={{ backgroundColor: '#79bd2f' }}
                color="#fff"
              />
            )
          );
        },
      },
      CG_BEFORE_PAR_EPRS_CNT: {
        makeHead: (headsMap) => {
          return (
            <SpanningTableContents
              topTitle={t('소재 변경 전 데이터')}
              type={'head'}
            >
              <div style={{ width: 50 }}>노출수</div>
              <div style={{ width: 50 }}>클릭수</div>
              <div style={{ width: 50 }}>CTR</div>
              <div style={{ width: 50 }}>세션전환단가</div>
              <div style={{ width: 50 }}>소진금액</div>
            </SpanningTableContents>
          );
        },
        makeBody: (rowObj, headsMap) => {
          const bodyItem1 = getHeadObj(headsMap, 'CG_BEFORE_PAR_EPRS_CNT');
          const bodyItem2 = getHeadObj(headsMap, 'CG_BEFORE_CLICK_CNT');
          const bodyItem3 = getHeadObj(headsMap, 'CG_BEFORE_CLICK_RATE');
          const bodyItem4 = getHeadObj(headsMap, 'CG_BEFORE_SESSION_CONV_AMT');
          const bodyItem5 = getHeadObj(headsMap, 'CG_BEFORE_ADVRTS_AMT');
          return (
            <SpanningTableContents>
              <div style={{ width: 50 }}>
                {makeRowColumn(
                  rowObj['CG_BEFORE_PAR_EPRS_CNT'],
                  bodyItem1.type,
                  bodyItem1.format
                )}
              </div>
              <div style={{ width: 50 }}>
                {makeRowColumn(
                  rowObj['CG_BEFORE_CLICK_CNT'],
                  bodyItem2.type,
                  bodyItem2.format
                )}
              </div>
              <div style={{ width: 50, color: theme.palette.table.cell.blue }}>
                {makeRowColumn(
                  rowObj['CG_BEFORE_CLICK_RATE'],
                  bodyItem3.type,
                  bodyItem3.format
                )}
              </div>
              <div style={{ width: 50, color: theme.palette.table.cell.red }}>
                {makeRowColumn(
                  rowObj['CG_BEFORE_SESSION_CONV_AMT'],
                  bodyItem4.type,
                  bodyItem4.format
                )}
              </div>
              <div style={{ width: 50, color: theme.palette.table.cell.red }}>
                {makeRowColumn(
                  rowObj['CG_BEFORE_ADVRTS_AMT'],
                  bodyItem5.type,
                  bodyItem5.format
                )}
              </div>
            </SpanningTableContents>
          );
        },
        headStyle: {
          borderLeft: '1px solid #e5e8eb',
          borderRight: '1px solid #e5e8eb',
        },
        bodyStyle: {
          borderLeft: '1px solid #e5e8eb',
          borderRight: '1px solid #e5e8eb',
        },
        orderFlag: false,
      },
      CG_BEFORE_CLICK_CNT: { merged: true },
      CG_BEFORE_ADVRTS_AMT: { merged: true },
      CG_BEFORE_CLICK_RATE: { merged: true },
      CG_BEFORE_SESSION_CONV_AMT: { merged: true },
      CG_AFTER_PAR_EPRS_CNT: {
        makeHead: (headsMap) => {
          return (
            <SpanningTableContents
              topTitle={t('소재 변경 후 데이터')}
              type={'head'}
            >
              <div style={{ width: 50 }}>노출수</div>
              <div style={{ width: 50 }}>클릭수</div>
              <div style={{ width: 50 }}>CTR</div>
              <div style={{ width: 50 }}>세션전환단가</div>
              <div style={{ width: 50 }}>소진금액</div>
            </SpanningTableContents>
          );
        },
        makeBody: (rowObj, headsMap) => {
          const bodyItem1 = getHeadObj(headsMap, 'CG_AFTER_PAR_EPRS_CNT');
          const bodyItem2 = getHeadObj(headsMap, 'CG_AFTER_CLICK_CNT');
          const bodyItem3 = getHeadObj(headsMap, 'CG_AFTER_CLICK_RATE');
          const bodyItem4 = getHeadObj(headsMap, 'CG_AFTER_SESSION_CONV_AMT');
          const bodyItem5 = getHeadObj(headsMap, 'CG_AFTER_ADVRTS_AMT');
          return (
            <SpanningTableContents>
              <div style={{ width: 50 }}>
                {makeRowColumn(
                  rowObj['CG_AFTER_PAR_EPRS_CNT'],
                  bodyItem1.type,
                  bodyItem1.format
                )}
              </div>
              <div style={{ width: 50 }}>
                {makeRowColumn(
                  rowObj['CG_AFTER_CLICK_CNT'],
                  bodyItem2.type,
                  bodyItem2.format
                )}
              </div>
              <div style={{ width: 50, color: theme.palette.table.cell.blue }}>
                {makeRowColumn(
                  rowObj['CG_AFTER_CLICK_RATE'],
                  bodyItem3.type,
                  bodyItem3.format
                )}
              </div>
              <div style={{ width: 50, color: theme.palette.table.cell.red }}>
                {makeRowColumn(
                  rowObj['CG_AFTER_SESSION_CONV_AMT'],
                  bodyItem4.type,
                  bodyItem4.format
                )}
              </div>
              <div style={{ width: 50, color: theme.palette.table.cell.red }}>
                {makeRowColumn(
                  rowObj['CG_AFTER_ADVRTS_AMT'],
                  bodyItem5.type,
                  bodyItem5.format
                )}
              </div>
            </SpanningTableContents>
          );
        },
        headStyle: {
          borderLeft: '1px solid #e5e8eb',
          borderRight: '1px solid #e5e8eb',
        },
        bodyStyle: {
          borderLeft: '1px solid #e5e8eb',
          borderRight: '1px solid #e5e8eb',
        },
        orderFlag: false,
      },
      CG_AFTER_CLICK_CNT: { merged: true },
      CG_AFTER_CLICK_RATE: { merged: true },
      CG_AFTER_SESSION_CONV_AMT: { merged: true },
      CG_AFTER_ADVRTS_AMT: { merged: true },
      CG_RAF_RATE_CLICK_RATE: {
        makeHead: (headsMap) => {
          return 'CTR' + t('증감');
        },
        makeBody: (rowObj, headsMap) => {
          const bodyItem1 = getHeadObj(headsMap, 'CG_RAF_RATE_CLICK_RATE');
          const color =
            rowObj['CG_RAF_RATE_CLICK_RATE'] > 200
              ? theme.palette.table.cell.red
              : theme.palette.table.cell.blue;
          return (
            <div style={{ color: color }}>
              {makeRowColumn(
                rowObj['CG_RAF_RATE_CLICK_RATE'],
                bodyItem1.type,
                bodyItem1.format
              )}
              %
            </div>
          );
        },
      },
      ADVER_BDGT_AMT: {
        makeBody: (rowObj, headsMap) => {
          const bodyItem1 = getHeadObj(headsMap, 'ADVER_BDGT_AMT');
          return (
            <div style={{ color: theme.palette.table.cell.red }}>
              {makeRowColumn(
                rowObj['ADVER_BDGT_AMT'],
                bodyItem1.type,
                bodyItem1.format
              )}
            </div>
          );
        },
      },
      CAMP_BDGT_AMT: {
        makeBody: (rowObj, headsMap) => {
          const bodyItem1 = getHeadObj(headsMap, 'CAMP_BDGT_AMT');
          return (
            <div style={{ color: theme.palette.table.cell.red }}>
              {makeRowColumn(
                rowObj['CAMP_BDGT_AMT'],
                bodyItem1.type,
                bodyItem1.format
              )}
            </div>
          );
        },
      },
      W_URL: {
        makeBody: (rowObj, headsMap) => {
          let stateColor = '#000';
          if (!!rowObj['W_URL']) {
            stateColor = '#f00';
          }
          return !!rowObj['W_URL'] ? (
            <NewButton
              title={t('사이트로 이동')}
              iconBtn
              icon="faHome"
              disableRipple
              onClick={(e) => handlePortalUrl(rowObj['W_URL'])}
            />
          ) : (
            <span style={{ color: stateColor }}>미등록</span>
          );
        },
      },
      M_URL: {
        makeBody: (rowObj, headsMap) => {
          let stateColor = '#000';
          if (!!rowObj['M_URL']) {
            stateColor = '#f00';
          }
          return !!rowObj['M_URL'] ? (
            <NewButton
              title={t('사이트로 이동')}
              iconBtn
              icon="faHome"
              disableRipple
              onClick={(e) => handlePortalUrl(rowObj['M_URL'])}
            />
          ) : (
            <span style={{ color: stateColor }}>미등록</span>
          );
        },
      },
      SITE_CODE: { merged: true },
      ADVER_ID: { merged: true },
    },
  };

  /**
   * modal 열어주는 함수
   */
  const handleModalOpen = (data) => {
    let newModal = { ...initModal };
    campaignManageImgList({ siteCode: data })
      .then((response) => {
        const { data } = response.data;
        newModal = {
          ...newModal,
          open: true,
          data: data,
        };
      })
      .catch((error) => {
        handleClickSnackbar({
          message: error,
          option: {
            type: 'error',
          },
        });
      })
      .finally(() => {
        setModal({
          ...newModal,
        });
      });
  };

  /**
   * 로그인처리
   */
  const handleOnLogin = (rowData) => {
    userOnLogin(
      rowData['ADVER_ID'],
      'manage',
      'https://manage.mobon.net/advertiser/form?' +
        'siteCode=' +
        rowData['SITE_CODE'] +
        '&svcType=' +
        params.advrtsPrdtCode
    )
      .then((response) => {
        const { data, success } = response.data;
        if (success) {
          handlePortalUrl(data.url);
        } else {
          handleClickSnackbar({
            message: t('로그인에 실패했습니다. 관리자에게 문의하세요.'),
            option: {
              type: 'error',
            },
          });
        }
      })
      .catch((error) => {
        handleClickSnackbar({
          message: error,
          option: {
            type: 'error',
          },
        });
      });
  };

  const handlePortalUrl = (url) => {
    let newPortal = portal.portal;
    let popIndex = newPortal.length;
    newPortal[popIndex] = { url: url, cnt: popIndex };
    dispatch(setPortal(newPortal));
  };

  /**
   * 모달 닫아주는 함수
   */
  const handleModalClose = () => {
    let newModal = { ...modal };
    newModal = {
      ...newModal,
      open: false,
    };
    setModal(newModal);
  };

  /**
   * 모달에 submit 버튼을 눌렀을 때 실행되는 함수
   */
  const handleSubmit = () => {
    setModal(initModal);
  };

  const handleClickSnackbar = useCallback((snackbarOptions) => {
    dispatch(showSuccessSnackbar(snackbarOptions));
    //eslint-disable-next-line
  }, []);

  return (
    <>
      <NewFilterWrap handleOnReset={handleOnReset}>
        <NewCommonFilter
          initialization={initialization}
          getParam={getParam}
          params={params}
          usePltfomTpCodeFilter={false}
          useExternalFilter={false}
          useManagerFilter
          pltfomTpCodeDetailFilter={['03', '04']}
          required={{
            type: 'DisplayFilter',
            val: ['advrtsPrdtCode', 'advrtsTpCode'],
          }}
          onlyProducts
          useDisplayAll={false}
        />
        <FilterCollection
          label={t('게재상태')}
          name="state"
          params={params}
          value={params.state}
          type="radio"
          list={list.state}
          callBack={getParam}
          labelKey="label"
          valueKey="value"
        />
        <FilterCollection
          label={t('상태필터')}
          name="changeState"
          params={params}
          value={params.changeState}
          type="radio"
          list={list.changeState}
          callBack={getParam}
          labelKey="label"
          valueKey="value"
        />
      </NewFilterWrap>
      <NewTableContainer
        loading={loading}
        className={classes.stateTable}
        customized={customized.stateTable}
        params={{
          sort: 'desc',
          order: 'STATS_DTTM',
        }}
        getParam={(f) => f}
        useCustomTable
        propsList={tableList}
        propsHeadCells={makeHeadData(tableHead.headList)}
        useTotal={false}
        useToolBar={false}
        useStoreLoading={false}
      />

      <NewTableContainer
        params={params}
        getParam={getParam}
        dataReqPromise={changeList}
        customized={customized.mainTable}
        useTotal={false}
        useFetch={false}
      />
      <Modal
        modal={modal}
        handleSubmit={handleSubmit}
        handleModalClose={handleModalClose}
      />
    </>
  );
};

export default Change;
